import {
  S3Client,
  PutObjectCommand,
  CreateMultipartUploadCommand,
  UploadPartCommand,
  CompleteMultipartUploadCommand,
  AbortMultipartUploadCommand,
} from '@aws-sdk/client-s3';
import axios from 'axios';

const PART_SIZE = 75 * 1024 * 1024;
const RETRY_LIMIT = 3;
const BACKOFF_BASE = 600;

const inferWindowOrigin = () => {
  if (typeof window === 'undefined' || !window.location) return '';
  const { protocol, host } = window.location;
  return `${protocol}//${host}`.replace(/\/$/, '');
};

const sanitizeBaseUrl = (url) => {
  if (!url) return '';
  const trimmed = url.replace(/\/$/, '');
  if (typeof window !== 'undefined' && window.location && window.location.protocol === 'https:' && trimmed.startsWith('http://')) {
    return `https://${trimmed.slice('http://'.length)}`;
  }
  return trimmed;
};

const resolveEnvBaseUrl = (explicitUrl) => {
  const candidates = [
    explicitUrl,
    process.env.REACT_APP_API_BASE_URL,
    process.env.REACT_APP_BORQS_URL,
  ];
  const envKey = (process.env.REACT_APP_ENVIRONMENT || '').toLowerCase();
  if (envKey === 'poc') candidates.push('https://poc.visist.ai');
  if (envKey === 'staging') candidates.push('https://stg.visist.ai');
  if (envKey === 'production') candidates.push('https://api-prod.visist.ai');
  candidates.push(inferWindowOrigin());
  return sanitizeBaseUrl(candidates.find(Boolean) || 'https://poc.visist.ai');
};

const normalizeAssetmgmtBaseUrl = (baseUrl) => {
  if (!baseUrl) return 'https://poc.visist.ai';
  if (baseUrl.includes(':9004')) {
    const protocolAdjusted = baseUrl.startsWith('http://') ? baseUrl.replace('http://', 'https://') : baseUrl;
    return sanitizeBaseUrl(protocolAdjusted.replace(':9004', ':9002'));
  }
  return sanitizeBaseUrl(baseUrl);
};

export class S3UploadService {
  constructor(apiBaseUrl) {
    this.apiBaseUrl = resolveEnvBaseUrl(apiBaseUrl);
    this.assetmgmtBaseUrl = normalizeAssetmgmtBaseUrl(this.apiBaseUrl);
    this.currentAbortController = null;
    this.isCancelled = false;
    this.abortReason = null;
    this.isPaused = false;
    this.pausePromise = null;
    this.resumeResolver = null;
  }

  resetCancellation() {
    this.isCancelled = false;
    this.abortReason = null;
    this.isPaused = false;
    this.resolvePauseWaiters();
  }

  recordCancellation(message = 'Upload cancelled by user') {
    if (!this.isCancelled) {
      this.isCancelled = true;
      this.abortReason = new Error(message);
      this.isPaused = false;
      this.resolvePauseWaiters();
    }
  }

  checkCancelled() {
    if (this.isCancelled) {
      throw this.abortReason || new Error('Upload cancelled by user');
    }
  }

  resolvePauseWaiters() {
    if (this.resumeResolver) {
      const resolve = this.resumeResolver;
      this.pausePromise = null;
      this.resumeResolver = null;
      resolve();
    }
  }

  async waitIfPaused() {
    if (!this.isPaused) return;
    if (!this.pausePromise) {
      this.pausePromise = new Promise((resolve) => {
        this.resumeResolver = resolve;
      });
    }
    await this.pausePromise;
  }

  async ensureActive() {
    this.checkCancelled();
    await this.waitIfPaused();
    this.checkCancelled();
  }

  pause() {
    if (this.isPaused) return;
    this.isPaused = true;
    if (!this.pausePromise) {
      this.pausePromise = new Promise((resolve) => {
        this.resumeResolver = resolve;
      });
    }
  }

  resume() {
    if (!this.isPaused) return;
    this.isPaused = false;
    this.resolvePauseWaiters();
  }

  cancel(message = 'Upload cancelled by user') {
    this.recordCancellation(message);
    this.abort();
  }

  setAbortController(controller) {
    if (this.currentAbortController && this.currentAbortController !== controller) {
      try {
        this.currentAbortController.abort();
      } catch (error) {}
    }
    this.currentAbortController = controller;
  }

  clearAbortController(controller) {
    if (!controller || this.currentAbortController === controller) {
      this.currentAbortController = null;
    }
  }

  async sendWithAbort(client, command) {
    const controller = new AbortController();
    this.setAbortController(controller);
    try {
      await this.ensureActive();
      return await client.send(command, { abortSignal: controller.signal });
    } catch (error) {
      if (this.isCancelled || error.name === 'AbortError') {
        this.recordCancellation();
        throw this.abortReason || new Error('Upload cancelled by user');
      }
      throw error;
    } finally {
      this.clearAbortController(controller);
    }
  }

  getAuthToken() {
    if (typeof window === 'undefined') return '';
    return localStorage.getItem('BQS_TOKEN') || '';
  }

  async getUploadCredentials(
    folderPath,
    maxFileSize = undefined,
    durationSeconds = undefined,
    draftPayload = undefined,
    filename = undefined
  ) {
    try {
      // Use /assetmgmt/post/draft endpoint to get fresh credentials
      const token = this.getAuthToken();

      const url = `${this.assetmgmtBaseUrl}/assetmgmt/post/draft`;

      console.log('🔐 Auth token present:', !!token);
      console.log('📡 Creating draft post and requesting credentials from:', url);

      const requestBody = {
        type: 'game',
        maxFileSize: maxFileSize || 104857600,
        durationSeconds: durationSeconds || 3600,
        ...(draftPayload || {})
      };

      if (filename && filename.trim()) {
        requestBody.filename = filename.trim();
      }

      console.log('📤 Request body:', requestBody);

      const response = await axios.post(url, requestBody, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      });

      console.log('📨 Credentials response status:', response.status);
      console.log('📨 Credentials response data:', response.data);

      if (!response.data) {
        throw new Error('Empty response from draft endpoint');
      }

      if (response.data.code && response.data.code !== 200) {
        throw new Error(response.data.message || `Draft creation failed with code ${response.data.code}`);
      }

      const credentialsData = response.data.data || response.data;

      if (!credentialsData.credentials || !credentialsData.s3Config) {
        console.error('Invalid response structure:', credentialsData);
        throw new Error('Invalid credentials response structure - missing credentials or s3Config');
      }

      console.log('✅ Draft post created with ID:', credentialsData.post.postId);
      console.log('📋 Post state:', credentialsData.post.state); // Should be "draft"
      console.log('📁 S3 Folder path:', credentialsData.s3Config.folderPath);
      console.log('⏰ Credentials fetched at:', new Date().toISOString());

      await this.ensureActive();
      return credentialsData;
    } catch (error) {
      console.error('❌ Failed to get upload credentials:', error.message);
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
      } else if (error.request) {
        console.error('No response received:', error.request);
      }
      throw error;
    }
  }

  async uploadPartWithRetry(client, bucket, key, partNumber, body, uploadId, maxAttempts = RETRY_LIMIT) {
    let attempt = 0;
    while (attempt < maxAttempts) {
      await this.ensureActive();
      try {
        const response = await this.sendWithAbort(
          client,
          new UploadPartCommand({
            Bucket: bucket,
            Key: key,
            PartNumber: partNumber,
            UploadId: uploadId,
            Body: body,
          })
        );
        return {
          PartNumber: partNumber,
          ETag: response.ETag,
        };
      } catch (error) {
        if (this.isCancelled || error.name === 'AbortError' || error.message === 'Upload cancelled by user') {
          throw this.abortReason || error;
        }
        attempt++;
        if (attempt >= maxAttempts) {
          throw error;
        }
        const delay = BACKOFF_BASE * Math.pow(2, attempt - 1);
        console.log(`Retrying part ${partNumber} in ${delay}ms (attempt ${attempt}/${maxAttempts})`);
        await new Promise((resolve) => setTimeout(resolve, delay));
        await this.ensureActive();
      }
    }
  }

  async finalizePost(postId, uploadedFiles) {
    try {
      const token = this.getAuthToken();
      const url = `${this.assetmgmtBaseUrl}/assetmgmt/post/${postId}/finalize`;

      console.log('📤 Finalizing post:', postId);
      console.log('📁 Uploaded files:', uploadedFiles);

      const response = await axios.patch(url, {
        files: uploadedFiles,
        generateThumbnail: true
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('✅ Post finalized successfully');
      console.log('📋 Post state:', response.data.data.state); // Should be "created"
      console.log('🎥 Video URLs:', response.data.data.videoUrls);

      return response.data.data;
    } catch (error) {
      console.error('❌ Failed to finalize post:', error.message);
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
      }
      throw error;
    }
  }

  async uploadFile(file, folderPath, onProgress = undefined, draftPayload = undefined) {
    this.resetCancellation();
    try {
      await this.ensureActive();
      console.log('🔑 Requesting upload credentials...');
      const credentialsData = await this.getUploadCredentials(
        folderPath,
        file.size,
        undefined,
        draftPayload,
        file.name
      );

      await this.ensureActive();
      const { credentials, s3Config, post } = credentialsData;

      console.log('✅ Credentials received');
      console.log('⏰ Using credentials fetched at:', new Date().toISOString());
      console.log('📦 Uploading to bucket:', s3Config.bucket);
      console.log('📁 Folder path:', s3Config.folderPath);
      console.log('🔑 S3 Key will be:', `${s3Config.folderPath}/${file.name}`);
      console.log('📄 File size:', file.size, 'bytes');

      const s3Client = new S3Client({
        region: s3Config.region,
        credentials: {
          accessKeyId: credentials.accessKeyId,
          secretAccessKey: credentials.secretAccessKey,
          sessionToken: credentials.sessionToken,
        },
      });

      const key = `${s3Config.folderPath}/${file.name}`;

      if (file.size < PART_SIZE) {
        console.log('🚀 Starting simple upload (file < 5MB)...');

        await this.ensureActive();
        const arrayBuffer = await file.arrayBuffer();
        await this.ensureActive();

        const response = await this.sendWithAbort(
          s3Client,
          new PutObjectCommand({
            Bucket: s3Config.bucket,
            Key: key,
            Body: arrayBuffer,
            ContentType: file.type || 'application/octet-stream',
            Metadata: {
              originalName: file.name,
              uploadedAt: new Date().toISOString(),
            },
          })
        );

        await this.ensureActive();

        if (onProgress) {
          onProgress({
            loaded: file.size,
            total: file.size,
            percentage: 100,
          });
        }

        console.log('✅ Upload complete!');

        await this.ensureActive();
        console.log('📋 Finalizing post with backend...');
        const finalizedPost = await this.finalizePost(post.postId, [{
          type: 'video',
          s3Path: key,
          fileName: file.name,
          fileSize: file.size
        }]);

        await this.ensureActive();
        const uploadResult = {
          success: true,
          key,
          location: `https://${s3Config.bucket}.s3.${s3Config.region}.amazonaws.com/${key}`,
          bucket: s3Config.bucket,
          etag: response.ETag || '',
          s3Config,
          postId: post.postId,
          postState: finalizedPost.state,
          videoUrls: finalizedPost.videoUrls
        };

        console.log('📤 Upload Response:', uploadResult);
        console.log('🔗 File URL:', uploadResult.location);
        console.log('🔑 S3 Key:', uploadResult.key);
        console.log('🎥 Video URLs:', uploadResult.videoUrls);
        console.log('📦 Full SDK Response:', response);

        return uploadResult;
      }

      console.log('🚀 Starting multipart upload...');
      await this.ensureActive();
      const createResponse = await this.sendWithAbort(
        s3Client,
        new CreateMultipartUploadCommand({
          Bucket: s3Config.bucket,
          Key: key,
          ContentType: file.type || 'application/octet-stream',
          Metadata: {
            originalName: file.name,
            uploadedAt: new Date().toISOString(),
          },
        })
      );

      const uploadId = createResponse.UploadId;
      const parts = [];
      let uploadedBytes = 0;

      const numParts = Math.ceil(file.size / PART_SIZE);
      console.log(`📦 Uploading in ${numParts} parts...`);

      for (let partNumber = 1; partNumber <= numParts; partNumber++) {
        await this.ensureActive();
        const start = (partNumber - 1) * PART_SIZE;
        const end = Math.min(start + PART_SIZE, file.size);
        const blob = file.slice(start, end);

        await this.ensureActive();
        const partArrayBuffer = await blob.arrayBuffer();
        await this.ensureActive();

        try {
          console.log(`📤 Uploading part ${partNumber}/${numParts}...`);
          const part = await this.uploadPartWithRetry(
            s3Client,
            s3Config.bucket,
            key,
            partNumber,
            partArrayBuffer,
            uploadId
          );
          parts.push(part);
          uploadedBytes += blob.size;

          await this.ensureActive();

          if (onProgress) {
            const percentage = Math.round((uploadedBytes / file.size) * 100);
            console.log(`📤 Upload progress: ${percentage}%`);
            onProgress({
              loaded: uploadedBytes,
              total: file.size,
              percentage,
            });
          }
        } catch (error) {
          const isCancelled = this.isCancelled || error.name === 'AbortError' || error.message === 'Upload cancelled by user';
          if (!isCancelled) {
            console.error(`❌ Failed to upload part ${partNumber}:`, error);
          }
          try {
            await s3Client.send(
              new AbortMultipartUploadCommand({
                Bucket: s3Config.bucket,
                Key: key,
                UploadId: uploadId,
              })
            );
            console.log('✅ Aborted multipart upload');
          } catch (abortError) {
            console.error('Failed to abort multipart upload:', abortError);
          }
          throw isCancelled ? (this.abortReason || error) : error;
        }
      }

      await this.ensureActive();
      const completeResponse = await this.sendWithAbort(
        s3Client,
        new CompleteMultipartUploadCommand({
          Bucket: s3Config.bucket,
          Key: key,
          UploadId: uploadId,
          MultipartUpload: {
            Parts: parts.sort((a, b) => a.PartNumber - b.PartNumber),
          },
        })
      );

      await this.ensureActive();
      console.log('✅ Upload complete!');
      console.log('📍 Location:', completeResponse.Location);

      await this.ensureActive();
      console.log('📋 Finalizing post with backend...');
      const finalizedPost = await this.finalizePost(post.postId, [{
        type: 'video',
        s3Path: key,
        fileName: file.name,
        fileSize: file.size
      }]);

      await this.ensureActive();
      const uploadResult = {
        success: true,
        key,
        location: completeResponse.Location || `https://${s3Config.bucket}.s3.${s3Config.region}.amazonaws.com/${key}`,
        bucket: s3Config.bucket,
        etag: completeResponse.ETag || '',
        s3Config,
        postId: post.postId,
        postState: finalizedPost.state, // Should be "created"
        videoUrls: finalizedPost.videoUrls // URLs ready to use!
      };

      console.log('📤 Multipart Upload Response:', uploadResult);
      console.log('🔗 File URL:', uploadResult.location);
      console.log('🔑 S3 Key:', uploadResult.key);
      console.log('🎥 Video URLs:', uploadResult.videoUrls);
      console.log('📦 Full SDK Response:', completeResponse);

      return uploadResult;
    } catch (error) {
      if (this.isCancelled || error.name === 'AbortError' || error.message === 'Upload cancelled by user') {
        console.warn('⚠️ Upload cancelled by user');
        throw this.abortReason || error;
      }

      console.error('❌ Upload failed:', error.message);
      console.error('❌ Error name:', error.name);
      console.error('❌ Error code:', error.code || error.$metadata?.httpStatusCode);

      if (error.message) {
        console.error('❌ Error details:', error.message);
      }

      if (error.$metadata) {
        console.error('❌ S3 Metadata:', error.$metadata);
      }

      try {
        const { s3Config } = await this.getUploadCredentials(folderPath, file?.size);
        if (s3Config) {
          console.error('❌ Failed S3 Key:', `${s3Config.folderPath}/${file?.name}`);
          console.error('❌ Bucket:', s3Config.bucket);
          console.error('❌ Region:', s3Config.region);
        }
      } catch {}

      throw error;
    } finally {
      this.clearAbortController();
    }
  }

  async uploadMultipleFiles(
    files,
    folderPath,
    onFileProgress = undefined,
    onOverallProgress = undefined
  ) {
    const results = [];
    let completed = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      console.log(`📤 Uploading file ${i + 1}/${files.length}: ${file.name}`);

      try {
        const result = await this.uploadFile(file, folderPath, (progress) => {
          if (onFileProgress) {
            onFileProgress(i, progress);
          }
        });
        results.push(result);
        completed++;

        if (onOverallProgress) {
          onOverallProgress(completed, files.length);
        }
      } catch (error) {
        console.error(`❌ Failed to upload ${file.name}:`, error);
        throw error;
      }
    }

    return results;
  }

  abort() {
    if (this.currentAbortController) {
      this.currentAbortController.abort();
    }
  }
}

export default S3UploadService;
